import { Alert, LinearProgress } from "@mui/material";
import { type GridColDef } from "@mui/x-data-grid-pro";
import { sortBy } from "lodash-es";
import type { FC } from "react";
import toast from "react-hot-toast";
import { useSupabaseCallback, useSupabaseLoader } from "../../../server/supabase/hooks";
import { notEmpty, propertyNotEmpty } from "../../../utils/not-empty";
import { CrudDataGrid } from "../../generic/components/crud-data-grid/crud-data-grid";
import type { Row } from "../../generic/components/crud-data-grid/types";
import {
  ACCOUNT_LIMIT_KEYS,
  type AccountLimitKey,
  type AdminAccountLimit,
  CeAdminSupabaseAccountService,
} from "../../generic/server/supabase-account-service";
import type { Columns, Keys } from "../types";
import { useTriggerPendoUpdate } from "./use-trigger-pendo-update";

export const AccountDataGrid: FC = () => {
  const { accountUpdated } = useTriggerPendoUpdate();

  const { data, loading, error } = useSupabaseLoader<
    { data: Columns[]; keys: string[] },
    "ce_admin"
  >(
    async ({ supabase }) => {
      const service = new CeAdminSupabaseAccountService(supabase);

      const [
        { data: accountLimitInfo, error: accountLimitInfoError },
        { data: limitKeys, error: limitKeysError },
      ] = await Promise.all([service.getAccountLimits(), service.getKeys()]);

      if (accountLimitInfoError || limitKeysError) {
        throw new Error("Could not load account limit info");
      }

      const keys = (limitKeys ?? []).filter(propertyNotEmpty("key")).map((x) => x.key);

      const makeColumns = (limitInfo: AdminAccountLimit["limits"]): Keys => {
        return Object.fromEntries(keys.map((key) => [key, limitInfo?.[key] ?? 0]));
      };

      const accountData: Columns[] = (accountLimitInfo ?? []).map(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ({ limits, account_type, industry, ...rest }) => ({
          ...rest,
          industry: industry ?? "",
          ...makeColumns(limits),
        }),
      );

      return {
        data: sortBy(accountData, "name"),
        keys,
      };
    },
    [],
    "ce_admin",
  );

  const persist = useSupabaseCallback(
    async ({ supabase }, row: Columns) => {
      const {
        id,
        name,
        business_name,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        account_type,
        usage,
        churned,
        enable_slack,
        enable_teams,
        enable_twilio,
        enable_work_email,
        enable_personal_email,
        industry,
        enable_tracking_pixel,
        ...limits
      } = row;

      const { error: err } = await new CeAdminSupabaseAccountService(supabase).setAccountLimits({
        account_id: id,
        limits: limits as { [key: string]: number },
        business_name,
        usage,
        churned,
        enable_slack,
        enable_teams,
        enable_twilio,
        enable_work_email,
        enable_personal_email,
        industry,
        enable_tracking_pixel,
      });

      if (err) {
        toast.error("Could not save account limit info");
        return;
      }

      toast.success(`Saved account limit info for ${name}`);

      await accountUpdated(row);

      return row;
    },
    [accountUpdated],
    "ce_admin",
  );

  if (loading) return <LinearProgress />;
  if (error) return <Alert severity="error">{error.message}</Alert>;

  return (
    <>
      <CrudDataGrid<Row<Columns>>
        initialRows={data.data}
        columns={{
          name: {
            headerName: "Account Name",
            width: 200,
            editable: false,
          },
          business_name: {
            headerName: "Business Name",
            width: 200,
            editable: true,
          },
          usage: {
            headerName: "Account Usage",
            width: 200,
            editable: true,
            type: "singleSelect",
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            valueOptions: ["CLIENT", "DEMO", "TESTING"],
          },
          industry: {
            headerName: "Industry",
            width: 400,
            editable: true,
            type: "singleSelect" as const,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            valueOptions: [
              { label: "Construction & Manufacturing", value: "Construction & Manufacturing" },
              {
                label: "Consulting & Professional Services",
                value: "Consulting & Professional Services",
              },
              { label: "Finance & Insurance", value: "Finance & Insurance" },
              { label: "Hospitals & Healthcare", value: "Hospitals & Healthcare" },
              { label: "Nonprofits & Education", value: "Nonprofits & Education" },
              { label: "Retail & Hospitality", value: "Retail & Hospitality" },
              { label: "Technology & IT", value: "Technology & IT" },
              { label: "Transport & Aviation", value: "Transport & Aviation" },
              { label: "Not Set", value: null },
            ],
          },
          churned: {
            headerName: "Churned",
            width: 200,
            editable: true,
            type: "boolean",
          },
          enable_slack: {
            headerName: "Enable Slack",
            width: 200,
            editable: true,
            type: "boolean",
          },
          enable_teams: {
            headerName: "Enable Teams",
            width: 200,
            editable: true,
            type: "boolean",
          },
          enable_twilio: {
            headerName: "Enable Twilio",
            width: 200,
            editable: true,
            type: "boolean",
          },
          enable_work_email: {
            headerName: "Enable Work Email",
            width: 200,
            editable: true,
            type: "boolean",
          },
          enable_personal_email: {
            headerName: "Enable Personal Email",
            width: 200,
            editable: true,
            type: "boolean",
          },
          enable_tracking_pixel: {
            headerName: "Enable Tracking Pixel",
            width: 200,
            editable: true,
            type: "boolean",
          },
          ...Object.fromEntries(data.keys.map(makeColumn).filter(notEmpty)),
        }}
        gridOverrides={{
          initialState: {
            pinnedColumns: { left: ["name"], right: ["actions"] },
            pagination: { paginationModel: { pageSize: 10 } },
            filter: {
              filterModel: {
                items: [{ id: "churned", field: "churned", operator: "is", value: false }],
              },
            },
          },
          pagination: true,
        }}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onUpdate={persist}
      />
    </>
  );
};

function makeColumn(key: string): [string, GridColDef<Columns>] {
  const def = ACCOUNT_LIMIT_KEYS[key as AccountLimitKey] ?? { type: "number" };

  // Build base column config
  const headerName = key
    .split("_")
    .map((x) => x[0]!.toUpperCase() + x.slice(1))
    .join(" ");

  if (def.type === "boolean") {
    return [
      key,
      {
        field: key,
        headerName,
        editable: true,
        width: 200,
        type: "boolean",
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        valueSetter: (value: boolean, row: Row<Columns>) => ({
          ...row,
          [key]: +value,
        }),
      },
    ];
  }

  if (def.type === "number") {
    return [
      key,
      {
        field: key,
        headerName,
        editable: true,
        width: 200,
        type: "number",
      },
    ];
  }

  if (def.type === "select") {
    const values = def.values as readonly string[];
    return [
      key,
      {
        headerName,
        editable: true,
        width: 200,
        type: "singleSelect",
        valueOptions: [...values],
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        valueSetter: (value: string, row: Row<Columns>) => ({
          ...row,
          [key]: values.indexOf(value),
        }),
        valueGetter: (value: number | undefined) => values[value || 0],
        field: key,
      },
    ];
  }

  // Fallback
  return [
    key,
    {
      field: key,
      headerName,
      editable: true,
      width: 200,
    },
  ];
}
